/**
 * RemoteBrowserContextMenu
 *
 * Custom context menu for the remote browser panel.
 * Provides Copy, Paste, Select All, and Reload actions.
 *
 * Features:
 * - Positioned at click/touch location
 * - Automatically closes when clicking outside
 * - Handles clipboard operations via the remote browser hook
 * - Works on both desktop (right-click) and mobile (long-press)
 */

import { useEffect, useRef, useCallback } from 'react';
import { Copy, ClipboardPaste, RefreshCw, TextCursorInput } from 'lucide-react';

export interface ContextMenuPosition {
  x: number;
  y: number;
}

export interface RemoteBrowserContextMenuProps {
  /** Menu position (screen coordinates) */
  position: ContextMenuPosition;
  /** Whether the menu is visible */
  isOpen: boolean;
  /** Callback when menu should close */
  onClose: () => void;
  /** Copy action - gets selection from remote browser and copies to local clipboard */
  onCopy: () => Promise<void>;
  /** Paste action - pastes from local clipboard to remote browser */
  onPaste: () => Promise<void>;
  /** Select all action */
  onSelectAll: () => void;
  /** Reload page action */
  onReload: () => void;
}

/**
 * Context menu item component
 */
function MenuItem({
  icon: Icon,
  label,
  onClick,
  disabled = false,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      className={`
        flex items-center gap-2 w-full px-3 py-2 text-sm text-left
        transition-colors duration-150
        ${
          disabled
            ? 'text-dark-muted cursor-not-allowed'
            : 'text-white/90 hover:bg-dark-surface/10 active:bg-dark-surface/20'
        }
      `}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon size={16} className="flex-shrink-0" />
      <span>{label}</span>
    </button>
  );
}

/**
 * Remote Browser Context Menu
 */
export function RemoteBrowserContextMenu({
  position,
  isOpen,
  onClose,
  onCopy,
  onPaste,
  onSelectAll,
  onReload,
}: RemoteBrowserContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Also close on Escape key
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Add listeners with a slight delay to avoid the same click/touch that opened the menu
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Adjust position to keep menu on screen
  const adjustedPosition = useCallback(() => {
    if (!menuRef.current) return position;

    const menuRect = menuRef.current.getBoundingClientRect();
    const padding = 8;

    let x = position.x;
    let y = position.y;

    // Keep menu within viewport
    if (x + menuRect.width > window.innerWidth - padding) {
      x = window.innerWidth - menuRect.width - padding;
    }
    if (y + menuRect.height > window.innerHeight - padding) {
      y = window.innerHeight - menuRect.height - padding;
    }
    if (x < padding) x = padding;
    if (y < padding) y = padding;

    return { x, y };
  }, [position]);

  // Handle copy action
  const handleCopy = useCallback(async () => {
    await onCopy();
    onClose();
  }, [onCopy, onClose]);

  // Handle paste action
  const handlePaste = useCallback(async () => {
    await onPaste();
    onClose();
  }, [onPaste, onClose]);

  // Handle select all action
  const handleSelectAll = useCallback(() => {
    onSelectAll();
    onClose();
  }, [onSelectAll, onClose]);

  // Handle reload action
  const handleReload = useCallback(() => {
    onReload();
    onClose();
  }, [onReload, onClose]);

  if (!isOpen) return null;

  const { x, y } = adjustedPosition();

  return (
    <div
      ref={menuRef}
      className="
        fixed z-[9999]
        min-w-[160px]
        bg-dark-surface/95 backdrop-blur-sm
        border border-dark-border/30
        rounded-xl shadow-2xl shadow-black/20
        py-1
        animate-in fade-in-0 zoom-in-95
        duration-100
      "
      style={{
        left: x,
        top: y,
      }}
    >
      <MenuItem icon={Copy} label="Copy" onClick={handleCopy} />
      <MenuItem icon={ClipboardPaste} label="Paste" onClick={handlePaste} />
      <div className="h-px bg-dark-surface/50/50 my-1" />
      <MenuItem icon={TextCursorInput} label="Select All" onClick={handleSelectAll} />
      <div className="h-px bg-dark-surface/50/50 my-1" />
      <MenuItem icon={RefreshCw} label="Reload" onClick={handleReload} />
    </div>
  );
}

export default RemoteBrowserContextMenu;
